import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { api } from '@src/app/app.const';
import { AppApiService } from '@src/app/core/services/app-api.service';
import { ZIP_CODE_VALIDATION_PATHS } from '@src/app/shared/components/address/address.constants';

export function formatAddress(addr: any): string | null {
  if (!addr) return null;
  const parts: string[] = [];
  const pushIf = (value: any) => {
    if (value === null || value === undefined) return;
    const s = String(value).trim();
    if (s.length) parts.push(s);
  };

  pushIf(addr.street);
  pushIf(addr.city);
  pushIf(addr.state || addr.province);
  pushIf(addr.postalCode);
  pushIf(addr.country);
  return parts.length ? parts.join(', ') : null;
}

export function formatAddressLine2(addr: any): string | null {
  if (!addr) return null;
  const parts: string[] = [];
  const pushIf = (value: any) => {
    if (value === null || value === undefined) return;
    const s = String(value).trim();
    if (s.length) parts.push(s);
  };

  pushIf(addr.city);
  pushIf(addr.state || addr.province);
  pushIf(addr.postalCode);
  pushIf(addr.country);
  return parts.length ? parts.join(', ') : null;
}

const US_STATE_CODES = new Set([
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
]);

export interface UsZipCodeValidationOptions {
  apiName?: string;
  path?: string;
  withoutToken?: boolean;
}

export function requiredTrimmedValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value || '').toString().trim();
  return value ? null : { required: true };
}

export function notOnlySpecialCharsValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value || '').toString().trim();
  if (!value) {
    return null;
  }

  return /[A-Za-z0-9]/.test(value) ? null : { onlySpecialChars: true };
}

export function noLeadingSpecialCharValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value || '').toString().trim();
  if (!value) {
    return null;
  }

  return /^[^A-Za-z0-9]/.test(value) ? { startsWithSpecialChar: true } : null;
}

export function usStateCodeValidator(control: AbstractControl): ValidationErrors | null {
  const rawValue = (control.value || '').toString();
  const value = rawValue.toUpperCase();
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  if (/\s/.test(rawValue)) {
    return { hasSpaces: true };
  }

  if (/[^A-Za-z]/.test(rawValue)) {
    return { invalidChars: true };
  }

  if (trimmedValue.length !== 2) {
    return { invalidLength: true };
  }

  return US_STATE_CODES.has(trimmedValue) ? null : { invalidStateCode: true };
}

export function usZipCodeValidator(
  appApiService: AppApiService,
  autoCompletedZipProvider: () => string | null,
  autoCompletedStateProvider: () => string | null,
  options: UsZipCodeValidationOptions = {}
): AsyncValidatorFn {
  return async (control: AbstractControl): Promise<ValidationErrors | null> => {
    const parent = control.parent;
    if (!parent) return null;

    const zipCode = (control.value || '').trim();
    const state = (parent.get('state')?.value || '').trim();

    if (!zipCode) return null;
    if (!/^\d{5}$/.test(zipCode)) return { invalidUsZipCodeFormat: true };

    const autoCompletedZip = autoCompletedZipProvider();
    const autoCompletedState = autoCompletedStateProvider();

    const isSameAutoCompletedAddress =
      autoCompletedZip !== null &&
      autoCompletedZip.trim() === zipCode &&
      (autoCompletedState || '').trim().toUpperCase() === state.toUpperCase();

    if (isSameAutoCompletedAddress) {
      return null;
    }

    const body = {
      code: zipCode,
      countryCode: 'US',
    };

    try {
      const apiName = options.apiName || api.systemConfiguration.name;
      const path = options.path || ZIP_CODE_VALIDATION_PATHS.default;
      const request = options.withoutToken ? appApiService.postWithoutToken : appApiService.post;
      const response = await request.call(appApiService, apiName, path, { body: body }, false);

      const endpointState = (response?.stateCode || '').trim().toUpperCase();
      const currentState = state.toUpperCase();

      if (endpointState && currentState && endpointState !== currentState) {
        return { stateMismatch: true };
      }

      return null;
    } catch (error: any) {
      const status = error?.response?.status || error?.response?.statusCode;

      if (status === 404) {
        return { invalidUsZipCode: true };
      }

      return { invalidUsZipCode: true };
    }
  };
}

export function usOnlyCountryValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value || '').toString().trim().toUpperCase();
  if (!value) {
    return null;
  }

  return value === 'US' ? null : { invalidCountry: true };
}
